import * as React from 'react';
import { useEffect, useRef, useState } from 'react';

/** Material UI Packages. */
import {
    Box,
    ButtonGroup,
    Button,
    ClickAwayListener,
    Grow,
    Paper,
    Popper,
    MenuList,
    MenuItem,
    Divider,
    IconButton,
    Popover,
    Tooltip,
} from '@mui/material';
import { useGridApiRef } from '@mui/x-data-grid-premium';

/** Material UI Icons. */
import AddIcon from '@mui/icons-material/Add';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import SearchIcon from '@mui/icons-material/Search';
import TuneIcon from '@mui/icons-material/Tune';

/** Dayjs Packages. */
import dayjs from 'dayjs';

/** Redux Packages. */
import { useDispatch, useSelector } from 'react-redux';

/** Redux Reducers. */
import { setProgessValueData } from 'shared/store/slices/progressValueSlice';
import { setCompleteDateWindow } from 'shared/store/slices/completeDateWindowSlice';
import { setSelectValueChange, resetSelectValue } from 'shared/store/slices/selectionSlice';
import { setSnackbar } from 'shared/store/slices/snackBarSlice';

/** Internal Components. */
import { progressTypeList } from 'shared/config/constantsData';
import ThemeButton from 'shared/ui/Button';
import DynamicToggleButton from 'shared/ui/ToggleButton';
import { deleteSocketData } from 'shared/lib/fetchSocket';
import { daysBetweenYmd, formatDateToDateString } from 'shared/lib/dateFormatter';
import DateWindowFields from 'shared/ui/DateWindowFields';

import FilterView from './FilterView/FilterView';
import NewShipmentDialog from './NewShipment/NewShipmentDialog';
import BulkNewShipmentDialog from './NewShipment/BulkNewShipmentDialog';
import {
    COMPLETE_WINDOW_DATE_FIELDS,
    COMPLETE_WINDOW_DEFAULT_FIELD,
    COMPLETE_WINDOW_MAX_DAYS,
    completeWindowError,
    defaultCompleteWindow,
} from '../lib/completeDateWindow';
import {
    shipmentDialogConfigs,
    shipmentFieldMappings,
    shipmentDeleteActions,
    formSetters,
} from 'data/controlsConfig';

/** The window shown in the picker: the one the operator picked, else the server default. */
const pickerValueFor = ({ start, end }) => {
    const shown = start && end ? { start, end } : defaultCompleteWindow();
    return [dayjs(shown.start), dayjs(shown.end)];
};

export default function ShipmentControls() {
    /** Get the dispatch function from Redux to send actions to the store. */
    const dispatch = useDispatch();

    /** State to manage the new shipment dialog's open/close. */
    const [newShipmentopen, setNewShipmentOpen] = useState(false);
    /** State to manage whether the new shipment operates as a copy. */
    const [hasCopy, setHasCopy] = useState(false);
    /** State to manage the bulk create dialog's open/close. */
    const [bulkOpen, setBulkOpen] = useState(false);
    /** State to manage the "NEW" split button's dropdown menu open/close. */
    const [newMenuOpen, setNewMenuOpen] = useState(false);
    /** Anchor ref for the "NEW" split button's dropdown menu. */
    const newMenuAnchorRef = useRef(null);

    /** Value for progress status(inprogress / complete / dispute). */
    const shipmentType = useSelector((state) => state.progressValue);
    /** Value for shipment type(OIM/OEX/AIM/AEX/Trucking = 0~4). */
    const tabValue = useSelector((state) => state.tabValueForm);
    /** Selected row ID from the main table. */
    const selectValue = useSelector((state) => state.mainTableSelectValue);
    /** Date window the COMPLETE grid is read with (OS-587). */
    const completeDateWindow = useSelector((state) => state.completeDateWindow);
    /** Picker draft (date column + range); applied to the grid only when SEARCH is pressed. */
    const [dateField, setDateField] = useState(completeDateWindow.field);
    const [dateValue, setDateValue] = useState(() => pickerValueFor(completeDateWindow));
    /** Anchor of the COMPLETE date-window popover; null while closed. */
    const [windowAnchor, setWindowAnchor] = useState(null);

    /** Keep the picker on the window the grid is actually showing. */
    useEffect(() => {
        setDateField(completeDateWindow.field);
        setDateValue(pickerValueFor(completeDateWindow));
    }, [completeDateWindow]);

    /** The draft spans more than a year — shown in red before SEARCH says so. */
    const draftOverOneYear = (() => {
        const [start, end] = dateValue;
        if (!start?.isValid() || !end?.isValid()) return false;
        const span = daysBetweenYmd(formatDateToDateString(start.toDate()), formatDateToDateString(end.toDate()));
        return span > COMPLETE_WINDOW_MAX_DAYS;
    })();

    /** Closing the popover without SEARCH discards the draft, so it reopens on the loaded window. */
    const handleWindowClose = () => {
        setWindowAnchor(null);
        setDateField(completeDateWindow.field);
        setDateValue(pickerValueFor(completeDateWindow));
    };

    /** Dialog's datagrid api ref. */
    const gridApiRef = useGridApiRef(null);

    const shipmentLists = {
        0: useSelector((state) => state.oimMainUpdateForm),
        1: useSelector((state) => state.oexMainUpdateForm),
        2: useSelector((state) => state.aimMainUpdateForm),
        3: useSelector((state) => state.aexMainUpdateForm),
        4: useSelector((state) => state.truckingMainUpdateForm),
    };

    /** Handle to open a new shipment dialog. */
    const handleClickOpen = () => {
        setNewShipmentOpen(true);
    };

    /** Handle to toggle the "NEW" split button's dropdown menu. */
    const handleNewMenuToggle = () => {
        setNewMenuOpen((prevOpen) => !prevOpen);
    };

    /** Handle to close the "NEW" split button's dropdown menu. */
    const handleNewMenuClose = (event) => {
        if (newMenuAnchorRef.current && newMenuAnchorRef.current.contains(event.target)) {
            return;
        }
        setNewMenuOpen(false);
    };

    /** Handle to open the bulk create dialog from the dropdown menu. */
    const handleBulkCreateClick = () => {
        setNewMenuOpen(false);
        setBulkOpen(true);
    };

    /** Handle for changing shipment type and reset a selection in main table. */
    const handleChange = (event, newAlignment) => {
        if (newAlignment !== null) {
            dispatch(setProgessValueData(newAlignment));
            dispatch(setSelectValueChange(0));
        }
    };

    /**
     * Handle to read the COMPLETE grid for the picked date window. Completed shipments
     * pile up without limit, so the window is capped at one year — same rule as the server.
     */
    const handleCompleteWindowSearch = () => {
        const [start, end] = dateValue.map((d) => (d && d.isValid() ? formatDateToDateString(d.toDate()) : null));
        const error = completeWindowError(start, end);
        if (error) return dispatch(setSnackbar({ open: true, message: error, severity: 'warning' }));
        dispatch(setCompleteDateWindow({ field: dateField, start, end }));
        dispatch(setSelectValueChange(0));
        setWindowAnchor(null);
    };

    /**
     * Handle to put the draft back to the initial condition: ETD, the past year up to
     * today. Only the draft changes — the popover stays open and the grid is read again
     * when SEARCH is pressed.
     */
    const handleCompleteWindowClear = () => {
        setDateField(COMPLETE_WINDOW_DEFAULT_FIELD);
        setDateValue(pickerValueFor({ start: null, end: null }));
    };

    /** Handle to delete a shipment entry. */
    const handleDeleteClick = () => {
        if (selectValue != 0) {
            if (window.confirm('Are you sure you want to delete the entry?')) {
                const { tableName, reducerName } = shipmentDeleteActions[tabValue];
                deleteSocketData(dispatch, tabValue, 'row', tableName, reducerName, selectValue, (response) => {
                    dispatch(setSnackbar({ open: true, message: 'Successfully deleted.', severity: 'success' }));
                    dispatch(resetSelectValue());
                });
            }
        } else
            dispatch(
                setSnackbar({
                    open: true,
                    message: 'Please select the shipment you want to delete.',
                    severity: 'warning',
                })
            );
    };

    /** Handle to copy a shipment entry. */
    const handleCopyClick = () => {
        const selectedShipment = shipmentLists[tabValue].find((item) => item.id == selectValue);
        if (!selectedShipment)
            return dispatch(
                setSnackbar({ open: true, message: 'Please select a shipment to copy.', severity: 'warning' })
            );

        shipmentFieldMappings[tabValue].forEach((field) => {
            const value = selectedShipment[field] ?? '';
            dispatch(formSetters[tabValue]({ [field === 'id' ? 'parent_id' : field]: value }));
        });

        setNewShipmentOpen(true);
        setHasCopy(true);
    };

    return (
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <DynamicToggleButton data={progressTypeList} onChange={handleChange} value={shipmentType} />
                {shipmentType === 'complete' && (
                    <>
                        <Tooltip title="Date range of completed shipments">
                            <IconButton
                                aria-label="complete date window"
                                onClick={(event) => setWindowAnchor(event.currentTarget)}
                                disableRipple
                                sx={{ p: 0, m: 0, color: windowAnchor ? '#00647e' : 'inherit' }}
                            >
                                <TuneIcon sx={{ fontSize: 22 }} />
                            </IconButton>
                        </Tooltip>
                        <Popover
                            open={Boolean(windowAnchor)}
                            anchorEl={windowAnchor}
                            onClose={handleWindowClose}
                            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                        >
                            <Box sx={{ p: '15px', display: 'flex', flexDirection: 'column', gap: 1.5, width: '280px' }}>
                                <DateWindowFields
                                    fields={COMPLETE_WINDOW_DATE_FIELDS}
                                    field={dateField}
                                    onFieldChange={setDateField}
                                    range={dateValue}
                                    onRangeChange={setDateValue}
                                    error={draftOverOneYear}
                                />
                                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                                    <ThemeButton
                                        text="CLEAR"
                                        width={'84px'}
                                        onClick={handleCompleteWindowClear}
                                        sx={{ border: '#00647e', bgcolor: 'white', color: '#00647e' }}
                                    />
                                    <ThemeButton
                                        text="SEARCH"
                                        icon={<SearchIcon />}
                                        width={'84px'}
                                        onClick={handleCompleteWindowSearch}
                                    />
                                </Box>
                            </Box>
                        </Popover>
                    </>
                )}
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
                <FilterView />
                <Divider
                    orientation="vertical"
                    flexItem
                    sx={{
                        borderWidth: '1px',
                    }}
                />
                <ButtonGroup variant="contained" ref={newMenuAnchorRef} sx={{ height: '27px', boxShadow: 'none' }}>
                    <ThemeButton
                        text="NEW"
                        icon={<AddIcon />}
                        width={'68px'}
                        onClick={handleClickOpen}
                        sx={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
                    />
                    <Button
                        size="small"
                        aria-controls={newMenuOpen ? 'new-shipment-split-button-menu' : undefined}
                        aria-expanded={newMenuOpen ? 'true' : undefined}
                        aria-label="select new shipment mode"
                        aria-haspopup="menu"
                        onClick={handleNewMenuToggle}
                        sx={{
                            color: 'white',
                            bgcolor: '#00647e',
                            minWidth: '22px',
                            width: '22px',
                            height: '27px',
                            borderTopLeftRadius: 0,
                            borderBottomLeftRadius: 0,
                            borderLeft: '1px solid rgba(255,255,255,0.4)',
                        }}
                    >
                        <ArrowDropDownIcon sx={{ fontSize: '18px' }} />
                    </Button>
                </ButtonGroup>
                <Popper
                    open={newMenuOpen}
                    anchorEl={newMenuAnchorRef.current}
                    role={undefined}
                    transition
                    disablePortal
                    placement="bottom-end"
                    sx={{ zIndex: 1300 }}
                >
                    {({ TransitionProps, placement }) => (
                        <Grow
                            {...TransitionProps}
                            style={{
                                transformOrigin: placement === 'bottom-end' ? 'right top' : 'right bottom',
                            }}
                        >
                            <Paper>
                                <ClickAwayListener onClickAway={handleNewMenuClose}>
                                    <MenuList id="new-shipment-split-button-menu" autoFocusItem={newMenuOpen}>
                                        <MenuItem onClick={handleBulkCreateClick}>Bulk Create</MenuItem>
                                    </MenuList>
                                </ClickAwayListener>
                            </Paper>
                        </Grow>
                    )}
                </Popper>
                <ThemeButton text="COPY" icon={<AddIcon />} width={'68px'} onClick={handleCopyClick} />
                <ThemeButton text="DELETE" width={'68px'} onClick={handleDeleteClick} />
            </Box>
            <NewShipmentDialog
                newShipmentopen={newShipmentopen}
                setNewShipmentOpen={setNewShipmentOpen}
                dialogConfig={shipmentDialogConfigs[tabValue]}
                hasCopy={hasCopy}
                setHasCopy={setHasCopy}
                gridApiRef={gridApiRef}
            />
            <BulkNewShipmentDialog
                open={bulkOpen}
                onClose={() => setBulkOpen(false)}
                dialogConfig={shipmentDialogConfigs[tabValue]}
                tabValue={tabValue}
            />
        </Box>
    );
}
